import React from 'react';
import DonutChart from 'shared/components/Graph/DonutChart';
import CountUpNumber from './CountUpNumber';
import DateRangeFilterPopover from './DateRangeFilterPopover';
import { COLORS } from '../utils/constants';
import { fluid } from '../utils/responsive';
import TeacherIcon from '../../../pages/userManagementV2/dashboardV3/teachers_piechart.svg';
import OperationIcon from '../../../pages/userManagementV2/dashboardV3/operations_personnel.svg';

const DONUT_SIZE = 191;
const DONUT_GAP_DEGREES = 13;

// GET /userv2/staff/stats actually responds with
// {"total":13,"byRole":{"Teacher":9,"Operation":3}} — byRole's keys are
// checked first since that's the real, confirmed shape; the other
// fallbacks are kept in case the API's field naming changes later.
// The API has no growth/percentage field of its own — just role counts —
// so each role's "%" is derived here as count / total. Kept as the exact
// value (decimals included, e.g. 66.67%) rather than rounded to a whole
// number, since rounding both figures independently can make them stop
// summing to the true 100%.
const toPct = (count, total) => {
    if (!total) return "0";
    const raw = (count / total) * 100;
    return Number.isInteger(raw) ? String(raw) : raw.toFixed(2);
};

const normalizeStaffStats = (staffStats) => {
    const totalStaff = staffStats?.totalStaff ?? staffStats?.total ?? 0;
    const teachersCount = staffStats?.byRole?.Teacher
        ?? staffStats?.byRole?.teacher
        ?? staffStats?.teachers?.count
        ?? staffStats?.teacherCount
        ?? 0;
    const operationsCount = staffStats?.byRole?.Operation
        ?? staffStats?.byRole?.Operations
        ?? staffStats?.byRole?.operation
        ?? staffStats?.byRole?.operations_personnel
        ?? staffStats?.operations?.count
        ?? staffStats?.operationsCount
        ?? Math.max(totalStaff - teachersCount, 0);
    return {
        totalStaff,
        teachers: {
            count: teachersCount,
            pct: toPct(teachersCount, totalStaff),
        },
        operations: {
            count: operationsCount,
            pct: toPct(operationsCount, totalStaff),
        },
    };
};

const OverviewCard = React.memo(({ accentColor, iconBg, icon, iconAlt, value, pct, label, isMobile }) => (
    <div className="dv2-overview-card" style={{
        width: "100%",
        flex: isMobile ? "none" : 1,
        minHeight: isMobile ? "auto" : 0,
        backgroundColor: COLORS.subtleBg,
        borderRadius: fluid(25),
        padding: isMobile ? "14px 16px" : `${fluid(20)} ${fluid(20)}`,
        display: "flex",
        alignItems: "center",
        gap: isMobile ? "16px" : fluid(14),
        boxSizing: "border-box",
    }}>
        <span className="dv2-overview-card-accent" style={{
            width: "6px",
            height: "48px",
            borderRadius: "2px",
            backgroundColor: accentColor,
            flexShrink: 0,
        }} />

        <div style={{ display: "flex", flexDirection: "column", gap: "2px", flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
                <CountUpNumber
                    value={value}
                    delay={0.3}
                    style={{ fontSize: "22px", fontWeight: 500, color: "#191919", lineHeight: 1.1, fontFamily: "Roboto, sans-serif" }}
                />
                <span style={{ fontSize: "15px", fontWeight: 400, color: "#636363", fontFamily: "Roboto, sans-serif" }}>
                    {pct}%
                </span>
            </div>
            <span style={{ fontSize: "14px", fontWeight: 400, color: "#636363", fontFamily: "Roboto, sans-serif" }}>
                {label}
            </span>
        </div>

        <div className="dv2-overview-card-icon" style={{
            width: isMobile ? "64px" : fluid(56),
            height: isMobile ? "64px" : fluid(56),
            borderRadius: isMobile ? "16px" : fluid(14),
            backgroundColor: iconBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
        }}>
            <img src={icon} alt={iconAlt} style={{ width: isMobile ? "32px" : fluid(28), height: isMobile ? "32px" : fluid(28) }} />
        </div>
    </div>
));

OverviewCard.displayName = 'OverviewCard';

const StaffDistributionCard = React.memo(({ isMobile = false, staffStats, onRangeChange }) => {
    const { totalStaff, teachers, operations } = normalizeStaffStats(staffStats);

    return (
        <div className="dv2-staffdist-card" style={{
            position: "relative",
            backgroundColor: "#FFFFFF",
            borderRadius: fluid(25),
            padding: isMobile ? "18px" : `${fluid(24)} ${fluid(28)}`,
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
            fontFamily: "Roboto, sans-serif",
        }}>
            <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: fluid(18),
                flexShrink: 0,
                gap: fluid(12),
                flexWrap: "wrap",
            }}>
                <div style={{ display: "flex", flexDirection: "column", gap: fluid(6) }}>
                    <span style={{ fontSize: fluid(24), fontWeight: 700, color: COLORS.textBlack }}>
                        Staff distribution
                    </span>
                    <div style={{ display: "flex", alignItems: "baseline", gap: fluid(8) }}>
                        <CountUpNumber
                            value={totalStaff}
                            delay={0.15}
                            style={{ fontSize: fluid(32), fontWeight: 700, color: COLORS.textBlack, lineHeight: 1.1 }}
                        />
                        <span style={{ fontSize: fluid(18), fontWeight: 400, color: COLORS.textLight }}>total staff</span>
                    </div>
                </div>

                <DateRangeFilterPopover onApply={onRangeChange} />
            </div>

            <div className="dv2-staffdist-body" style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: "center",
                gap: isMobile ? "20px" : "36px",
                flex: 1,
                minHeight: 0,
            }}>
                <div style={{
                    display: "flex",
                    justifyContent: "center",
                    flexShrink: 0,
                    minWidth: 0,
                    width: isMobile ? "100%" : "170px",
                    maxWidth: isMobile ? "100%" : "170px",
                }}>
                    <DonutChart
                        fluid
                        size={170}
                        strokeWidth={14}
                        gapDegrees={DONUT_GAP_DEGREES}
                        segments={(teachers.count === 0 && operations.count === 0) ? [
                            { value: 50, color: COLORS.chartBlue },
                            { value: 50, color: COLORS.chartGreen },
                        ] : [
                            { value: teachers.count, color: COLORS.chartBlue },
                            { value: operations.count, color: COLORS.chartGreen },
                        ]}
                    >
                        <span style={{ fontSize: fluid(16), fontWeight: 400, color: COLORS.textLight }}>Types</span>
                        <span style={{ fontSize: fluid(20), fontWeight: 700, color: COLORS.textBlack, marginTop: fluid(12) }}>2</span>
                    </DonutChart>
                </div>

                <div style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: isMobile ? "20px" : fluid(20),
                    width: "100%",
                    minWidth: 0,
                    flex: 1,
                    height: isMobile ? undefined : "194px",
                }}>
                    <OverviewCard
                        accentColor={COLORS.chartBlue}
                        iconBg={COLORS.chartBlueLight}
                        icon={TeacherIcon}
                        iconAlt="Teachers"
                        value={teachers.count}
                        pct={teachers.pct}
                        label="teachers"
                        isMobile={isMobile}
                    />
                    <OverviewCard
                        accentColor={COLORS.chartGreen}
                        iconBg={COLORS.chartGreenLight}
                        icon={OperationIcon}
                        iconAlt="Operations personnel"
                        value={operations.count}
                        pct={operations.pct}
                        label="operations personnel"
                        isMobile={isMobile}
                    />
                </div>
            </div>
        </div>
    );
});

StaffDistributionCard.displayName = 'StaffDistributionCard';

export default StaffDistributionCard;
